<script setup>
/**
 * Hand the chat to a person — the note that person reads, and who that person is.
 *
 * ── THE NOTE ──────────────────────────────────────────────────────────────────────────
 * `HandoffStep.note` is `.min(1)` in the sidecar's schema — guidance for the private note left
 * for whoever picks the chat up. It is not customer-facing, which is why there is no Arabic
 * box on this screen: the person reading it works here.
 *
 * ── WHO GETS IT (task review item 11) ─────────────────────────────────────────────────
 * "Hand to": the account's own "Hand chats to" (no `handTo` at all, the default), a person or
 * a team, from the same people and teams Personality's picker reads (`…/handover/people`). A
 * chosen person or team gets every chat that reaches this step, even one somebody else holds;
 * a team that picks nobody keeps the chat for all its members. "Add labels" picks from the
 * account's own labels (Chatwoot's store). A choice that is gone from the account is said on
 * the screen, and the BFF refuses to save it (`unknown_person`, `unknown_team`).
 *
 * ── AND NOTHING FOLLOWS IT ────────────────────────────────────────────────────────────
 * A `next` on a hand-over is dead in the engine and round-trips as a real "then", so this
 * screen never offers one and the list never draws one. A step screen that showed an onward
 * link here would be offering a control whose only effect is to be stored.
 *
 * `end` shares this screen. An `end` step reads back as a step that finishes the task, its
 * optional `instruction` is the last thing said, and it round-trips exactly — which is what
 * lets "Finish" stop being a kind an owner has to pick without rewriting anybody's graph.
 */
import { computed, getCurrentInstance, onMounted } from 'vue';

import WasloTextArea from '../WasloTextArea.vue';
import WasloSelect from '../WasloSelect.vue';
import HandoverLabels from '../HandoverLabels.vue';
import { useWasloI18n } from '../../i18n.js';
import { useWasloApi } from '../../composables/useWasloApi.js';
import { useWasloAccountId } from '../../composables/useWasloAccountId.js';
import { wasloApi } from '../../api/client.js';
import { MAX_INSTRUCTION, MAX_NOTE } from '../../helpers/task-flow-draft.js';
import { handToChoiceOf, handToPatch } from '../../helpers/hand-to.js';

const props = defineProps({
  /** A spine row whose step is a `handoff` or an `end`. */
  row: { type: Object, required: true },
});

const emit = defineEmits(['update:row']);

const { t } = useWasloI18n();

const key = name => `WASLO.TASKS.COMPOSE.HANDOFF.${name}`;

const step = computed(() => props.row.step ?? {});
const isEnd = computed(() => step.value.type === 'end');

const patchStep = changes => {
  const next = { ...step.value, ...changes };
  // Absent is the account's setting and no labels: a key left as `undefined` or `[]` would
  // round-trip as a change nobody made.
  if (next.handTo === undefined) delete next.handTo;
  if (Array.isArray(next.labels) && next.labels.length === 0)
    delete next.labels;
  emit('update:row', { ...props.row, step: next });
};

// ── who gets it ───────────────────────────────────────────────────────────────────────
const accountId = useWasloAccountId();
const people = useWasloApi(
  signal => wasloApi.getHandoverPeople(accountId.value, signal),
  { isEmpty: () => false, immediate: !isEnd.value }
);
// Chatwoot's store, for the account's labels. Absent in a harness.
const store = getCurrentInstance()?.proxy?.$store ?? null;
onMounted(() => {
  if (!isEnd.value) store?.dispatch?.('labels/get')?.catch?.(() => {});
});
const labelOptions = computed(() =>
  (store?.getters?.['labels/getLabels'] ?? [])
    .map(label => label?.title)
    .filter(title => typeof title === 'string' && title !== '')
);

const choice = computed(() => handToChoiceOf(step.value));
const HAND_TO = ['account', 'person', 'team'];
const handToOptions = computed(() =>
  HAND_TO.map(id => ({
    value: id,
    label: t(key(`HAND_TO_${id.toUpperCase()}`)),
  }))
);
const agents = computed(() => people.data.value?.agents ?? []);
const teams = computed(() => people.data.value?.teams ?? []);
const targetOptions = computed(() =>
  (choice.value.to === 'team' ? teams.value : agents.value).map(row => ({
    value: String(row.id),
    label: row.name,
  }))
);

const selectHandTo = to => {
  if (!HAND_TO.includes(to) || to === choice.value.to) return;
  patchStep(handToPatch(to, null));
};
const selectTarget = value =>
  patchStep(handToPatch(choice.value.to, Number(value) || null));

/** The chosen person or team is not among the account's any more. Said only once the list is in. */
const targetGone = computed(() => {
  const { to, id } = choice.value;
  if (to === 'account' || id === null || !people.data.value) return false;
  const list = to === 'team' ? teams.value : agents.value;
  return !list.some(row => row.id === id);
});

/**
 * The chosen person cannot see a channel the assistant answers: Personality's warning, word for
 * word (`HandChatsTo.vue`), so a step cannot quietly send a chat to someone who never sees it.
 */
const notMember = computed(() => {
  if (choice.value.to !== 'person') return '';
  const chosen = agents.value.find(agent => agent.id === choice.value.id);
  if (!chosen || !(chosen.missingInboxes ?? []).length) return '';
  const channels = chosen.missingInboxes
    .map(inbox => inbox.name)
    .filter(Boolean)
    .join(t('WASLO.COMMON.LIST_SEPARATOR'));
  return t('WASLO.PERSONALITY.HAND_TO.NOT_MEMBER', {
    name: chosen.name,
    channels,
  });
});

const labels = computed(() =>
  Array.isArray(step.value.labels) ? step.value.labels : []
);
</script>

<template>
  <div class="flex flex-col gap-6" data-waslo-handoff-step>
    <WasloTextArea
      v-if="!isEnd"
      :model-value="step.note ?? ''"
      :label="t(key('NOTE'))"
      :hint="t(key('NOTE_HINT'))"
      :placeholder="t(key('NOTE_PLACEHOLDER'))"
      :max-length="MAX_NOTE"
      min-height="4rem"
      data-waslo-handoff-note
      @update:model-value="value => patchStep({ note: value })"
    />
    <div v-if="!isEnd" class="flex flex-col gap-3" data-waslo-handoff-route>
      <WasloSelect
        :model-value="choice.to"
        :label="t(key('HAND_TO'))"
        :options="handToOptions"
        data-waslo-handoff-hand-to
        @update:model-value="selectHandTo"
      />
      <p
        v-if="choice.to === 'account'"
        class="mb-0 text-sm text-n-slate-11"
        data-waslo-handoff-hand-to-hint
      >
        {{ t(key('HAND_TO_ACCOUNT_HINT')) }}
      </p>
      <template v-else>
        <p
          v-if="people.error.value"
          class="mb-0 text-sm text-n-ruby-11"
          data-waslo-handoff-people-failed
        >
          {{ t('WASLO.PERSONALITY.HAND_TO.PEOPLE_FAILED') }}
        </p>
        <WasloSelect
          v-else
          :model-value="choice.id === null ? '' : String(choice.id)"
          :label="t(key(choice.to === 'team' ? 'WHICH_TEAM' : 'WHICH_PERSON'))"
          :placeholder="
            t(
              choice.to === 'team'
                ? 'WASLO.PERSONALITY.HAND_TO.CHOOSE_TEAM'
                : 'WASLO.PERSONALITY.HAND_TO.CHOOSE'
            )
          "
          :options="targetOptions"
          data-waslo-handoff-target
          @update:model-value="selectTarget"
        />
        <p
          v-if="targetGone"
          class="mb-0 text-sm text-n-amber-11"
          data-waslo-handoff-target-gone
        >
          {{ t(key(choice.to === 'team' ? 'TEAM_GONE' : 'PERSON_GONE')) }}
        </p>
        <p
          v-if="notMember"
          class="mb-0 text-sm text-n-amber-11"
          dir="auto"
          data-waslo-handoff-not-member
        >
          {{ notMember }}
        </p>
        <p class="mb-0 text-sm text-n-slate-11" data-waslo-handoff-wins>
          {{ t(key(choice.to === 'team' ? 'TEAM_HINT' : 'PERSON_HINT')) }}
        </p>
      </template>
      <HandoverLabels
        :model-value="labels"
        :options="labelOptions"
        @update:model-value="value => patchStep({ labels: value })"
      />
    </div>
    <WasloTextArea
      v-else
      :model-value="step.instruction ?? ''"
      :label="t(key('LAST_WORD'))"
      :hint="t(key('LAST_WORD_HINT'))"
      :max-length="MAX_INSTRUCTION"
      min-height="4rem"
      data-waslo-handoff-last-word
      @update:model-value="value => patchStep({ instruction: value })"
    />
    <p class="mb-0 text-sm text-n-slate-11" data-waslo-handoff-note-end>
      {{ t(key(isEnd ? 'ENDS' : 'AFTER')) }}
    </p>
  </div>
</template>
